import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';
import ReportingPage from '@/pages/ReportingPage';
import { useSettings } from '@/contexts/SettingsContext';
import { tokenStore } from '../lib/tokenStore';
import {
  TrendingUp, Users, Euro, Target, Download, FileSpreadsheet, FileText, FileSignature, Car
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getUmsatzToken } from '@/lib/umsatz';
import { Badge } from '@/components/ui/badge';
import { UmsatzBlock, UmsatzWert, useUmsatz } from '@/components/UmsatzSperre';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Cell, PieChart, Pie
} from 'recharts';

const RANGES = [
  { value: '7d', label: '7 Tage' },
  { value: '30d', label: '30 Tage' },
  { value: '90d', label: '90 Tage' },
  { value: 'all', label: 'Gesamt' },
];

const SOURCE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6B7280'];

function ExportButtons({ reportType, range, token }) {
  const excelUrl = reportsAPI.exportExcel(reportType, { time_range: range });
  const pdfUrl = reportsAPI.exportPDF(reportType, { time_range: range });

  const download = async (url, ext) => {
    const res = await fetch(url, { headers: { ...tokenStore.getAuthHeaders(), ...(getUmsatzToken() ? { 'X-Umsatz-Token': getUmsatzToken() } : {}) }, credentials: 'include' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${reportType}_${range}.${ext}`;
    a.click();
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => download(excelUrl, 'xlsx')} data-testid={`export-excel-${reportType}`}>
        <FileSpreadsheet className="w-3.5 h-3.5 text-green-600" /> Excel
      </Button>
      <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => download(pdfUrl, 'pdf')} data-testid={`export-pdf-${reportType}`}>
        <FileText className="w-3.5 h-3.5 text-red-500" /> PDF
      </Button>
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = 'primary' }) {
  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border/40">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5 text-muted-foreground/50" />}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [range, setRange] = useState('30d');
  const [convData, setConvData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [revData, setRevData] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const [vertragData, setVertragData] = useState(null);
  const [parkData, setParkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suchparameter, setSuchparameter] = useSearchParams();
  const [tab, setTabState] = useState(suchparameter.get('tab') || 'conversion');
  const setTab = (t) => { setTabState(t); setSuchparameter(t === 'conversion' ? {} : { tab: t }, { replace: true }); };
  const { stageMap, formatCurrency } = useSettings();
  const { frei: umsatzFrei } = useUmsatz();   // Umsatz-Sperre (30.10.2026): clientseitige Summen nur mit Freigabe
  const geldWert = (v) => <UmsatzWert wert={v} format={formatCurrency} />;
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { time_range: range };
      const [c, a, r, s] = await Promise.all([
        reportsAPI.leadConversion(params),
        reportsAPI.agentPerformance(params),
        reportsAPI.revenue(params),
        reportsAPI.leadSourceROI(params),
      ]);
      setConvData(c.data); setAgentData(a.data); setRevData(r.data); setSourceData(s.data);
      // Parkplatznutzung (eigener Try — fehlt der Endpoint, bleiben die anderen Tabs intakt)
      try {
        const p = await reportsAPI.parking(params);
        setParkData(p.data);
      } catch (pErr) {  }
      // Fetch contract stats
      try {
        const vRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vertraege`, { headers: { ...tokenStore.getAuthHeaders() } });
        if (vRes.ok) {
          const vd = await vRes.json();
          const vertraege = vd.vertraege || [];
          const aktiv = vertraege.filter(v => v.status === 'aktiv').length;
          const gekuendigt = vertraege.filter(v => v.status === 'gekuendigt' || v.status === 'gekündigt').length;
          const totalMR = vertraege.filter(v => v.status === 'aktiv').reduce((s, v) => s + (v.monthly_price || 0), 0);
          // dieselbe Regel wie alle Auswertungen (backend/kennzahlen/regeln.py): Bestand und Storno sind kein Abschluss
          const BESTAND = ['bestand_digitalisiert', 'papier_foto', 'neue_laufzeit_bestand'];
          const neu = vertraege.filter(v => v.status !== 'storniert' && !BESTAND.includes(v.herkunft)).length;
          setVertragData({ total: vertraege.length, neu, aktiv, gekuendigt, totalMonthlyRevenue: totalMR, vertraege });
        }
      } catch (vErr) {  }
    } catch (err) {  }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rangeLabel = RANGES.find(r => r.value === range)?.label || range;

  return (
    <div className="space-y-5 fade-in max-w-6xl" data-testid="reports-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Berichte</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Analysen und Leistungskennzahlen</p>
        </div>
        <div className="flex items-center gap-1.5 bg-muted/50 rounded-lg p-1">
          {RANGES.map((r) => (
            <button key={r.value} onClick={() => setRange(r.value)} data-testid={`report-range-${r.value}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${range === r.value ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={`skeleton-${i}`} className="h-48 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="conversion" className="gap-1.5 text-xs"><TrendingUp className="w-3.5 h-3.5" />Konversion</TabsTrigger>
            <TabsTrigger value="agents" className="gap-1.5 text-xs"><Users className="w-3.5 h-3.5" />Team</TabsTrigger>
            <TabsTrigger value="revenue" className="gap-1.5 text-xs"><Euro className="w-3.5 h-3.5" />Umsatz</TabsTrigger>
            <TabsTrigger value="vertraege" className="gap-1.5 text-xs"><FileSignature className="w-3.5 h-3.5" />Verträge</TabsTrigger>
            <TabsTrigger value="sources" className="gap-1.5 text-xs"><Target className="w-3.5 h-3.5" />Quellen</TabsTrigger>
            {/* nur wenn Parkplatz-Kurse eingerichtet sind — sonst ein leerer Reiter (12.10.2026) */}
            {parkData?.configured && <TabsTrigger value="parking" className="gap-1.5 text-xs"><Car className="w-3.5 h-3.5" />Parkplatz</TabsTrigger>}
            <TabsTrigger value="kampagnen" className="gap-1.5 text-xs" data-testid="tab-kampagnen"><Target className="w-3.5 h-3.5" />Kampagnen</TabsTrigger>
          </TabsList>

          {/* ─── Lead Conversion ─── */}
          <TabsContent value="conversion" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Konversions-Trichter</h3>
              <ExportButtons reportType="lead-conversion" range={range} />
            </div>
            {convData && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Leads in der Pipeline" value={convData.total_leads} sub="ohne Archiv" icon={Users} />
                  <MetricCard label="Gewinnrate" value={`${convData.win_rate}%`} sub={`${convData.won} Abschlüsse / ${convData.lost} verloren im Zeitraum`} icon={TrendingUp} />
                  <MetricCard label="Neu im Zeitraum" value={convData.new_in_range} sub={rangeLabel} icon={Target} />
                  <MetricCard label="Gewonnen" value={convData.won} icon={Euro} />
                </div>
                <Card className="border-border/60">
                  <CardContent className="pt-4">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={convData.stages} margin={{ left: 0, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="stage" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => stageMap[v]?.label || v.charAt(0).toUpperCase() + v.slice(1)} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {(convData.stages || []).map((s, i) => <Cell key={s.stage || `stage-${i}`} fill={stageMap[s.stage]?.color || '#6B7280'} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                {/* Stage breakdown table */}
                <Card className="border-border/60 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Stufe</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Anzahl</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">% Anteil</th>
                      <th className="px-4 py-2.5 font-medium text-muted-foreground w-48"></th>
                    </tr></thead>
                    <tbody>
                      {convData.stages.map((s) => (
                        <tr key={s.stage} className="border-b border-border/30">
                          <td className="px-4 py-2.5 flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm" style={{ background: stageMap[s.stage]?.color }} />
                            <span className="font-medium capitalize">{stageMap[s.stage]?.label || s.stage}</span>
                          </td>
                          <td className="text-right px-4 py-2.5 mono">{s.count}</td>
                          <td className="text-right px-4 py-2.5">{s.percentage}%</td>
                          <td className="px-4 py-2.5">
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className="h-2 rounded-full transition-all" style={{ width: `${s.percentage}%`, background: stageMap[s.stage]?.color }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Agent Performance ─── */}
          <TabsContent value="agents" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Team-Leistung</h3>
              <ExportButtons reportType="agent-performance" range={range} />
            </div>
            {agentData && (
              <>
                <Card className="border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted/30">
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Wer</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Leads</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Gewonnen</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Verloren</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Konv.</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Aktivitäten</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Umsatz</th>
                      </tr></thead>
                      <tbody>
                        {agentData.agents.map((a, i) => (
                          <tr key={a.user_id} className="border-b border-border/30 hover:bg-muted/20">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">{a.name?.charAt(0)}</div>
                                <div>
                                  <p className="font-medium text-xs">{a.name}</p>
                                  <p className="text-[10px] text-muted-foreground capitalize">{a.role_name || a.role}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-center px-3 py-3">{a.total_leads}</td>
                            <td className="text-center px-3 py-3 font-medium text-green-600">{a.won}</td>
                            <td className="text-center px-3 py-3 text-red-500">{a.lost}</td>
                            <td className="text-center px-3 py-3">
                              <Badge variant={a.conversion_rate >= 50 ? 'default' : 'secondary'} className="text-[10px]">{a.conversion_rate}%</Badge>
                            </td>
                            <td className="text-center px-3 py-3">{a.activities}</td>
                            <td className="text-right px-4 py-3 font-medium mono">{geldWert(a.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
                {/* Agent chart */}
                <Card className="border-border/60">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Abschlüsse je Person</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={agentData.agents.filter(a => a.won > 0)} margin={{ left: 0, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        <Bar dataKey="won" fill="#22C55E" radius={[4, 4, 0, 0]} name="Gewonnen" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Revenue ─── */}
          <TabsContent value="revenue" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Umsatzbericht</h3>
              <ExportButtons reportType="revenue" range={range} />
            </div>
            {revData && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Vertragswert der Abschlüsse" value={geldWert(revData.total_revenue)} sub="Beitrag × Laufzeit" icon={Euro} />
                  <MetricCard label="Pipeline-Wert (geschätzt)" value={geldWert(revData.pipeline_value)} sub="offene Leads × Ø Vertragswert" icon={TrendingUp} />
                  <MetricCard label="Gewonnen" value={revData.won_deals} icon={Target} />
                  <MetricCard label="Durchschn. Wert" value={geldWert(revData.avg_deal_size)} icon={Euro} />
                </div>
                <UmsatzBlock gesperrt={revData.umsatz_gesperrt} titel="Pipeline-Werte">
                <Card className="border-border/60">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Pipeline-Wert pro Stufe (geschätzt)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={revData.stage_values} margin={{ left: 20, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="stage" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => stageMap[v]?.label || v.charAt(0).toUpperCase() + v.slice(1)} />
                        <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {(revData.stage_values || []).map((s, i) => <Cell key={s.stage || `stage-${i}`} fill={stageMap[s.stage]?.color || '#6B7280'} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                </UmsatzBlock>
              </>
            )}
          </TabsContent>

          {/* ─── Verträge ─── */}
          <TabsContent value="vertraege" className="mt-4 space-y-4">
            <h3 className="text-sm font-semibold">Vertragsstatistiken</h3>
            {vertragData && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Verträge im CRM" value={vertragData.total} sub={`davon ${vertragData.neu} neue Abschlüsse`} icon={FileSignature} />
                  <MetricCard label="Aktive Verträge" value={vertragData.aktiv} sub="laufend" icon={Target} />
                  <MetricCard label="Gekündigte" value={vertragData.gekuendigt} icon={Users} />
                  <MetricCard label="Monatsbeiträge" value={<UmsatzWert wert={umsatzFrei ? vertragData.totalMonthlyRevenue : null} format={(v) => `${v.toFixed(2).replace('.', ',')} €`} />} sub="Beiträge dieser Verträge, nicht aller Mitglieder" icon={Euro} />
                </div>
                <Card className="border-border/60">
                  <CardContent className="pt-4">
                    <p className="text-xs text-muted-foreground mb-3">Verträge nach Tarif</p>
                    <div className="space-y-2">
                      {(() => {
                        const byPlan = {};
                        (vertragData.vertraege || []).forEach(v => {
                          const k = v.plan_name || 'Unbekannt';
                          if (!byPlan[k]) byPlan[k] = { count: 0, active: 0, revenue: 0 };
                          byPlan[k].count++;
                          if (v.status === 'aktiv') { byPlan[k].active++; byPlan[k].revenue += v.monthly_price || 0; }
                        });
                        const entries = Object.entries(byPlan).sort((a, b) => b[1].count - a[1].count);
                        const maxCount = Math.max(...entries.map(([, v]) => v.count), 1);
                        return entries.map(([plan, stats]) => (
                          <div key={plan} className="flex items-center gap-3">
                            <div className="w-36 text-xs font-medium truncate text-right flex-shrink-0">{plan}</div>
                            <div className="flex-1 relative">
                              <div className="h-7 rounded-md bg-marke flex items-center px-3 transition-all" style={{ width: `${Math.max(20, (stats.count / maxCount) * 100)}%` }}>
                                <span className="text-[11px] font-semibold text-white">{stats.count}× ({stats.active} aktiv)</span>
                              </div>
                            </div>
                            <div className="w-20 text-right text-xs font-bold text-muted-foreground flex-shrink-0"><UmsatzWert wert={umsatzFrei ? stats.revenue : null} format={(v) => `${v.toFixed(2).replace('.', ',')} €`} kurz /></div>
                          </div>
                        ));
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ─── Lead Source ROI ─── */}
          <TabsContent value="sources" className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold">Quellen-Auswertung</h3>
              <ExportButtons reportType="lead-source-roi" range={range} />
            </div>
            {sourceData && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-border/60">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Volumen pro Quelle</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={sourceData.sources} dataKey="total_leads" nameKey="source" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} label={({ source, total_leads }) => `${source?.replace(/_/g, ' ')} (${total_leads})`} labelLine={false}>
                            {sourceData.sources.map((_, i) => <Cell key={`src-${i}`} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                          </Pie>
                          <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="border-border/60 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted/30">
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Quelle</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Leads</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Gewonnen</th>
                        <th className="text-center px-3 py-2.5 font-medium text-muted-foreground">Konv.</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Umsatz</th>
                      </tr></thead>
                      <tbody>
                        {sourceData.sources.map((s, i) => (
                          <tr key={s.source} className="border-b border-border/30">
                            <td className="px-4 py-2.5 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                              <span className="capitalize text-xs">{s.source?.replace(/_/g, ' ')}</span>
                            </td>
                            <td className="text-center px-3 py-2.5">{s.total_leads}</td>
                            <td className="text-center px-3 py-2.5 text-green-600 font-medium">{s.won}</td>
                            <td className="text-center px-3 py-2.5"><Badge variant={s.conversion_rate >= 50 ? 'default' : 'secondary'} className="text-[10px]">{s.conversion_rate}%</Badge></td>
                            <td className="text-right px-4 py-2.5 mono font-medium">{geldWert(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* ─── Parkplatznutzung ─── */}
          <TabsContent value="parking" className="mt-4 space-y-4">
            <h3 className="text-sm font-semibold">Parkplatznutzung</h3>
            {!parkData || !parkData.configured ? (
              <Card className="border-border/60">
                <CardContent className="pt-6 pb-6 text-center text-sm text-muted-foreground">
                  Keine Parkplatz-Systemkurse gefunden. Im Kursplan zwei Kurse mit Kategorie „parking" anlegen
                  („Parkplatz Fest" / „Parkplatz Variabel"), dann erscheinen hier die Belegungszahlen aus der App.
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard
                    label="Fest belegt (heute)"
                    value={`${parkData.today.fest.occupied} / ${parkData.today.fest.total}`}
                    sub="aktive Belegungen"
                    icon={Car}
                  />
                  <MetricCard
                    label="Variabel belegt (heute)"
                    value={`${parkData.today.variabel.occupied} / ${parkData.today.variabel.total}`}
                    sub="aktive Belegungen"
                    icon={Car}
                  />
                  <MetricCard
                    label="Nutzung im Zeitraum"
                    value={parkData.total_usage}
                    sub={`Belegungen · ${rangeLabel}`}
                    icon={TrendingUp}
                  />
                  <MetricCard
                    label="Auslastung"
                    value={`${parkData.utilization}%`}
                    sub={`von ${parkData.capacity.fest + parkData.capacity.variabel} Plätzen/Tag`}
                    icon={Target}
                  />
                </div>
                <Card className="border-border/60">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Belegungen pro Tag</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={parkData.days} margin={{ left: 0, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          tickFormatter={(v) => v?.slice(8, 10) + '.' + v?.slice(5, 7) + '.'} />
                        <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} />
                        <RechartsTooltip
                          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                          labelFormatter={(v) => v?.slice(8, 10) + '.' + v?.slice(5, 7) + '.' + v?.slice(0, 4)}
                        />
                        <Bar dataKey="fest" stackId="p" fill="#6B7280" name="Fest" />
                        <Bar dataKey="variabel" stackId="p" fill="#E60000" radius={[4, 4, 0, 0]} name="Variabel" />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex gap-4 justify-center mt-2">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#6B7280' }} />Fest</span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#E60000' }} />Variabel</span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
          {/* Reporting & Attribution (v2 B4) — seit 18.09.2026 hier als Tab statt eigener Seite */}
          <TabsContent value="kampagnen" className="mt-4">
            <ReportingPage eingebettet />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
